import React, { useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { toJS } from 'mobx'
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit'
import moment from 'moment'
import Papa from 'papaparse'
import { toast } from 'react-toastify'
import LeaderboardsStore from '../../store/LeaderboardsPage.store'
import AdminApi from '../../../../api/admin-api/admin-api.js'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import SystemSettingStore from '../../../../shared/stores/systemSetting.store'

import './ExportBreakTheBarButton.component.scss'

// Maps each agent group to its API key and system setting scope.
// On export, one API call is made per group to fetch weekly points data.
const AGENT_TYPES = [
  { key: 'user_id', scope: 'sig_agent', label: 'Sig Agents' },
  { key: 'sub_agent_id', scope: 'career_agent', label: 'Career Agents' },
  { key: 'elite_agency_id', scope: 'elite_agency_team', label: 'Elite Teams' },
  { key: 'district', scope: 'district', label: 'District Teams' },
  { key: 'region', scope: 'region', label: 'Region Teams' },
  { key: 'division', scope: 'division', label: 'Division Teams' },
]

// Weekly column names returned by the leaderboard API in weekly output mode.
// Used by computeDayBrokeBar to determine cumulative daily totals.
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/**
 * Looks up the historically-accurate break-the-bar threshold for a given
 * agent scope and week start date. Thresholds change over time; the history
 * array in system setting 'usertypes---break-the-bar-thresholds' tracks
 * each change with a start date and value.
 *
 * Returns the most recent threshold whose start date <= the given startDate.
 * Falls back to the earliest historical value if none match.
 */
const getThresholdForScope = (scope, startDate) => {
  const btb = SystemSettingStore.get('usertypes---break-the-bar-thresholds')
  if (!btb || !Array.isArray(btb[scope]?.history)) return 0

  // Sort descending by date so the most recent threshold is first.
  const history = [...btb[scope].history].sort((a, b) =>
    moment(b.start, 'YYYY-MM-DD').diff(moment(a.start, 'YYYY-MM-DD'))
  )

  // Find thresholds that were active on or before the viewed week.
  const applicable = history.filter(
    th => moment(startDate, 'YYYY-MM-DD').diff(moment(th.start, 'YYYY-MM-DD'), 'days') >= 0
  )

  // If no threshold predates the viewed week, fall back to the earliest entry.
  if (!applicable.length && history.length) return parseInt(history[0]?.value) || 0
  return parseInt(applicable[0]?.value) || 0
}

/**
 * Determines which day of the week an agent's cumulative points first
 * reached or exceeded the threshold. Walks Monday through Sunday,
 * summing daily values until the threshold is met.
 * Returns the day name (e.g. "Wednesday") or empty string if never reached.
 */
const computeDayBrokeBar = (row, threshold) => {
  let cumulative = 0
  for (const day of WEEKDAYS) {
    const val = parseFloat(row[day]) || 0
    cumulative += val
    if (cumulative >= threshold) return day
  }
  return ''
}

const ExportBreakTheBarButton = observer(() => {
  const [isExporting, setIsExporting] = useState(false)
  // Synchronous guard to prevent duplicate exports from rapid clicks.
  // useState is async (batched), so a second click can fire before
  // React re-renders with the disabled button. The ref blocks immediately.
  const exportingRef = useRef(false)

  const fetchParams = toJS(LeaderboardsStore.fetchParams)
  const isBST = UserProfileService.isA(['internal-admin', 'internal-staff', 'system-admin'])
  // BST-only, weekly+points only. Self-gates so parent components
  // don't need conditional rendering logic.
  const isWeeklyPoints = fetchParams.output_mode === 'weekly' && fetchParams.type === 'points'

  /**
   * Fetches weekly points data for all 6 agent groups in parallel,
   * filters to agents meeting their group's threshold, and generates
   * a CSV download. Reads fetchParams fresh at call time (not from
   * render closure) to avoid stale data after date navigation.
   */
  const exportCsv = async () => {
    if (exportingRef.current) return
    exportingRef.current = true
    setIsExporting(true)

    try {
      // Read params fresh at click time to get the latest date range.
      const params = toJS(LeaderboardsStore.fetchParams)
      const { start, stop, categories } = params
      const opts = params.opts || {}

      // Fire 6 parallel API calls, one per agent group.
      const results = await Promise.all(
        AGENT_TYPES.map(async ({ key, scope, label }) => {
          try {
            // Always fetch weekly output mode regardless of current view,
            // since break-the-bar is a weekly metric.
            const result = await AdminApi.getLeaderboardData('points', start, stop, categories, key, 'weekly', opts)
            const rows = result?.data?.payload?.rows || []
            const threshold = getThresholdForScope(scope, start)

            return rows
              .filter(
                row => row.u_name !== 'Totals:' && !isNaN(parseFloat(row.total)) && parseFloat(row.total) >= threshold
              )
              .map(row => [
                label,
                row.rank || '',
                row.u_name || '',
                row.div_name || '',
                row.total,
                threshold,
                computeDayBrokeBar(row, threshold),
                row.ytd_points || '',
              ])
          } catch (ex) {
            console.error(`Failed to fetch ${label} leaderboard:`, ex)
            return []
          }
        })
      )

      const allRows = results.flat()

      if (!allRows.length) {
        toast.info('No agents broke the bar this week across any group.', {
          position: toast.POSITION.TOP_CENTER,
        })
        exportingRef.current = false
        setIsExporting(false)
        return
      }

      const weekNum = moment(start, 'YYYY-MM-DD').isoWeek()
      const year = moment(start, 'YYYY-MM-DD').year()
      const startFmt = moment(start, 'YYYY-MM-DD').format('MMM DD')
      const stopFmt = moment(stop, 'YYYY-MM-DD').format('MMM DD')

      const columns = [
        'Agent Group',
        'Rank',
        'Agent Name',
        'Division',
        'Total Points',
        'Threshold',
        'Day Broke Bar',
        'YTD Points',
      ]

      // Client-side CSV generation via PapaParse + Blob download pattern.
      const csv = Papa.unparse([columns, ...allRows])
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `Break-The-Bar-Wk${String(weekNum).padStart(2, '0')}-${startFmt.replace(/\s/g, '')}-${stopFmt.replace(
          /\s/g,
          ''
        )}-${year}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (ex) {
      console.error('Export failed:', ex)
      toast.error('Failed to generate export. Please try again.', {
        position: toast.POSITION.TOP_CENTER,
      })
    }

    exportingRef.current = false
    setIsExporting(false)
  }

  if (!isBST || !isWeeklyPoints) return null

  return (
    <div id='ExportBreakTheBarButtonComponent' className='mt-1'>
      <MDBBtn
        type='button'
        color='default'
        block
        size='sm'
        disabled={isExporting || LeaderboardsStore.isFetching}
        onClick={exportCsv}
      >
        {isExporting ? (
          <>
            <MDBIcon icon='spinner' spin /> Exporting...
          </>
        ) : (
          <>
            <MDBIcon icon='file-csv' /> Export Bar Breakers
          </>
        )}
      </MDBBtn>
    </div>
  )
})

export default ExportBreakTheBarButton
